import type { InventoryItem } from '../types/inventory'
import { validateMix, readMix } from './feedMix'
import { readFeedRules, type UnitFeedRules } from './feedRules'

// Office Plans v0 (pre-validation PR 6, docs/crew-mode/prd/crew-mode-office-plans-v0.md):
// per-unit plans the office sets in Editar instalación and Crew Mode only
// reads (Charter O-4), kept in `ProductionUnit.metadata` (no migration).
//
// - `metadata.feedPlan`        { itemId, kgPerDay, feedingsPerDay } — itemId may be a mix;
//                               findings #182 adds `mode` (how the crew records feed) and
//                               `secondItem` { itemId, kgPerDay } (a second feed, e.g. a size
//                               transition). Both optional: older phones read the first feed.
// - `metadata.samplingPlan`    { fishCount, measureLength, lotId? }
// - `metadata.waterParameters` ordered list of the parameters to ask
// - `metadata.feedRules`       findings #211: when not to feed + oxygen settings (src/utils/feedRules.ts)

/**
 * Findings #182: how the crew records feed in a unit, set by the office.
 * - `perFeeding` "Por toma" (default, as before): each feeding, "Dado según plan" = kg/día ÷ tomas.
 * - `dayTotal` "Total del día": one entry for the day's amount.
 * - `weighed` "Pesada del día": the ration weighed in the morning, the leftover weighed at
 *   the end of the day; fed = pesado − sobrante.
 */
export type FeedPlanMode = 'perFeeding' | 'dayTotal' | 'weighed'
export const FEED_PLAN_MODES: FeedPlanMode[] = ['perFeeding', 'dayTotal', 'weighed']
export type UnitFeedPlanItem = { itemId: string; kgPerDay: number }
export type UnitFeedPlan = {
  itemId: string
  kgPerDay: number
  feedingsPerDay: number
  mode?: FeedPlanMode
  secondItem?: UnitFeedPlanItem
}
export type UnitSamplingPlan = { fishCount: number; measureLength: boolean; lotId?: string }
export type UnitWaterParameter = 'temperature' | 'ph' | 'dissolvedOxygen' | 'salinity' | 'flow'

/** Every parameter the office can ask for. Flow (Hatchery v0, #84) is asked only when ticked. */
export const UNIT_WATER_PARAMETERS: UnitWaterParameter[] = ['temperature', 'ph', 'dissolvedOxygen', 'salinity', 'flow']
/** What Crew Mode asks when the office ticked nothing. */
export const DEFAULT_WATER_PARAMETERS: UnitWaterParameter[] = ['temperature', 'ph', 'dissolvedOxygen', 'salinity']

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const readMetadata = (value: WithMetadata): Record<string, unknown> => {
  const metadata = value?.metadata
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {}
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const positiveNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const readFeedPlan = (unit: WithMetadata): UnitFeedPlan | null => {
  const raw = readMetadata(unit).feedPlan
  if (!isPlainObject(raw)) return null
  const itemId = typeof raw.itemId === 'string' ? raw.itemId.trim() : ''
  const kgPerDay = positiveNumber(raw.kgPerDay)
  const feedingsPerDay = positiveNumber(raw.feedingsPerDay)
  if (!itemId || kgPerDay === null || feedingsPerDay === null || !Number.isInteger(feedingsPerDay)) return null
  const mode = typeof raw.mode === 'string' && (FEED_PLAN_MODES as string[]).includes(raw.mode) && raw.mode !== 'perFeeding'
    ? raw.mode as FeedPlanMode
    : undefined
  const second = isPlainObject(raw.secondItem) ? raw.secondItem : null
  const secondItemId = second && typeof second.itemId === 'string' ? second.itemId.trim() : ''
  const secondKg = second ? positiveNumber(second.kgPerDay) : null
  const secondItem = secondItemId && secondItemId !== itemId && secondKg !== null ? { itemId: secondItemId, kgPerDay: secondKg } : undefined
  return { itemId, kgPerDay, feedingsPerDay, ...(mode ? { mode } : {}), ...(secondItem ? { secondItem } : {}) }
}

/** Findings #182: the plan's recording mode ("Por toma" when not set). */
export const feedPlanMode = (plan: UnitFeedPlan | null | undefined): FeedPlanMode => plan?.mode ?? 'perFeeding'

/** Findings #182: the plan's feeds with their kg/día — one, or two during a transition. */
export const feedPlanItems = (plan: UnitFeedPlan | null | undefined): UnitFeedPlanItem[] => (
  plan ? [{ itemId: plan.itemId, kgPerDay: plan.kgPerDay }, ...(plan.secondItem ? [plan.secondItem] : [])] : []
)

const round3 = (value: number): number => Math.round(value * 1000) / 1000

/** Findings #182: the plan's kg/día over all its feeds. */
export const feedPlanTotalKg = (plan: UnitFeedPlan): number => round3(feedPlanItems(plan).reduce((total, item) => total + item.kgPerDay, 0))

/**
 * Findings #182: a plan with a new total kg/día, each feed keeping its share
 * (the first feed takes the rounding remainder). Used by "Usar recomendado".
 */
export const withFeedPlanTotalKg = (plan: UnitFeedPlan, totalKg: number): UnitFeedPlan => {
  if (!plan.secondItem) return { ...plan, kgPerDay: round3(totalKg) }
  const currentTotal = feedPlanTotalKg(plan)
  const secondKg = round3(currentTotal > 0 ? (plan.secondItem.kgPerDay / currentTotal) * totalKg : totalKg / 2)
  return { ...plan, kgPerDay: round3(totalKg - secondKg), secondItem: { ...plan.secondItem, kgPerDay: secondKg } }
}

/** The per-feeding ration: kg per day ÷ feedings per day, rounded to 3 decimals. */
export const rationPerFeeding = (plan: UnitFeedPlan): number =>
  Math.round((plan.kgPerDay / plan.feedingsPerDay) * 1000) / 1000

/**
 * Findings #182: what one tap of the plan button records for a feed — its
 * kg/día ÷ tomas in "Por toma", its whole kg/día in the other modes.
 */
export const plannedKgFor = (plan: UnitFeedPlan, item: UnitFeedPlanItem): number => (
  feedPlanMode(plan) === 'perFeeding' ? round3(item.kgPerDay / plan.feedingsPerDay) : round3(item.kgPerDay)
)

/**
 * The plan's feed item when the plan is still usable: the item exists in the
 * farm's feed items and, for a mix, the recipe is valid. Otherwise null, so
 * Crew Mode falls back to "last feed" (C1, C2).
 */
export const resolvePlanFeedItem = (plan: UnitFeedPlan | null, feedItems: InventoryItem[]): InventoryItem | null => {
  if (!plan) return null
  const item = feedItems.find((candidate) => candidate.id === plan.itemId)
  if (!item || item.category !== 'feed') return null
  const mix = readMix(item)
  if (mix && !validateMix(mix, feedItems).ok) return null
  return item
}

/**
 * Findings #182: the plan's feeds that are still usable, with their inventory
 * item. A stale first feed drops the whole plan (as before); a stale second
 * feed is left out.
 */
export const resolvePlanFeedItems = (
  plan: UnitFeedPlan | null,
  feedItems: InventoryItem[]
): Array<{ item: InventoryItem; planItem: UnitFeedPlanItem }> => {
  const first = resolvePlanFeedItem(plan, feedItems)
  if (!plan || !first) return []
  const out = [{ item: first, planItem: { itemId: plan.itemId, kgPerDay: plan.kgPerDay } }]
  if (plan.secondItem) {
    const second = resolvePlanFeedItem({ ...plan, itemId: plan.secondItem.itemId }, feedItems)
    if (second) out.push({ item: second, planItem: plan.secondItem })
  }
  return out
}

export const readSamplingPlan = (unit: WithMetadata): UnitSamplingPlan | null => {
  const raw = readMetadata(unit).samplingPlan
  if (!isPlainObject(raw)) return null
  const fishCount = positiveNumber(raw.fishCount)
  if (fishCount === null || !Number.isInteger(fishCount)) return null
  const lotId = typeof raw.lotId === 'string' && raw.lotId.trim() ? raw.lotId.trim() : undefined
  return { fishCount, measureLength: raw.measureLength === true, ...(lotId ? { lotId } : {}) }
}

/**
 * A sampling plan applies to the unit's current lot unless it names another lot.
 * Findings #177: a plan naming a lot that is no longer in the unit (moved,
 * merged, deleted) applies to the unit's current lot instead of hiding the plan.
 * `unitLotIds` = the lots now in the unit; omitted = the named lot is trusted.
 */
export const samplingPlanForLot = (
  unit: WithMetadata,
  lotId: string | null | undefined,
  unitLotIds?: readonly string[]
): UnitSamplingPlan | null => {
  const plan = readSamplingPlan(unit)
  if (!plan) return null
  const namedLotStillHere = !unitLotIds || (plan.lotId ? unitLotIds.includes(plan.lotId) : true)
  if (plan.lotId && lotId && plan.lotId !== lotId && namedLotStillHere) return null
  return plan
}

/** Findings #177: the plan's lot when it is still one of `lotIds`, otherwise '' (= the unit's current lot). */
export const samplingPlanLotIdIn = (plan: UnitSamplingPlan | null, lotIds: readonly string[]): string =>
  plan?.lotId && lotIds.includes(plan.lotId) ? plan.lotId : ''

/** The parameters the office asked for, in its order. Empty when none were set. */
export const readWaterParameters = (unit: WithMetadata): UnitWaterParameter[] => {
  const raw = readMetadata(unit).waterParameters
  if (!Array.isArray(raw)) return []
  const out: UnitWaterParameter[] = []
  for (const entry of raw) {
    if ((UNIT_WATER_PARAMETERS as string[]).includes(entry) && !out.includes(entry as UnitWaterParameter)) {
      out.push(entry as UnitWaterParameter)
    }
  }
  return out
}

/** What the water screen offers: the asked parameters, or all four when none were set. */
export const waterParametersToAsk = (unit: WithMetadata): UnitWaterParameter[] => {
  const asked = readWaterParameters(unit)
  return asked.length > 0 ? asked : [...DEFAULT_WATER_PARAMETERS]
}

export const withUnitPlans = <T extends NonNullable<WithMetadata>>(
  value: T,
  plans: {
    feedPlan?: UnitFeedPlan | null
    samplingPlan?: UnitSamplingPlan | null
    waterParameters?: UnitWaterParameter[] | null
    feedRules?: UnitFeedRules | null
  }
): T => {
  const metadata = { ...readMetadata(value) }
  if (plans.feedPlan === null) delete metadata.feedPlan
  else if (plans.feedPlan) metadata.feedPlan = { ...plans.feedPlan }
  if (plans.samplingPlan === null) delete metadata.samplingPlan
  else if (plans.samplingPlan) metadata.samplingPlan = { ...plans.samplingPlan }
  if (plans.waterParameters === null || (plans.waterParameters && plans.waterParameters.length === 0)) delete metadata.waterParameters
  else if (plans.waterParameters) metadata.waterParameters = [...plans.waterParameters]
  if (plans.feedRules === null || (plans.feedRules && Object.keys(plans.feedRules).length === 0)) delete metadata.feedRules
  else if (plans.feedRules) metadata.feedRules = { ...plans.feedRules }
  return { ...value, metadata }
}

/**
 * "Copiar a otras unidades del lote" (findings #101): the office sets one unit's
 * plans and copies them to the other units holding the same lot (same fish, same
 * water). Feed item, feedings/day, sampling, water parameters and the feed rules are copied;
 * kg/day stays each unit's own unless `copyKgPerDay` (a unit with no feed plan
 * yet takes the source's kg/day so its plan is complete). The sampling lot is
 * kept only when the target holds that lot.
 */
export const copyUnitPlans = <T extends NonNullable<WithMetadata>>(
  source: WithMetadata,
  target: T,
  options: { copyKgPerDay: boolean; targetLotIds: string[] }
): T => {
  const sourceFeed = readFeedPlan(source)
  const targetFeed = readFeedPlan(target)
  const sourceSampling = readSamplingPlan(source)
  // Findings #182: the recording mode and the second feed are copied too; without
  // "copiar kg/día" the target keeps its own total, split like the source.
  const feedPlan: UnitFeedPlan | null = sourceFeed
    ? (options.copyKgPerDay || !targetFeed
      ? { ...sourceFeed }
      : withFeedPlanTotalKg({ ...sourceFeed }, feedPlanTotalKg(targetFeed)))
    : null
  const samplingPlan: UnitSamplingPlan | null = sourceSampling
    ? {
        fishCount: sourceSampling.fishCount,
        measureLength: sourceSampling.measureLength,
        ...(sourceSampling.lotId && options.targetLotIds.includes(sourceSampling.lotId) ? { lotId: sourceSampling.lotId } : {})
      }
    : null
  const water = readWaterParameters(source)
  // Findings #211: the stop rules and oxygen settings travel with the plan.
  return withUnitPlans(target, { feedPlan, samplingPlan, waterParameters: water.length > 0 ? water : null, feedRules: readFeedRules(source) })
}
